import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { PRODUCT_OPTIONS } from "@/data/content";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", company: "", phone: "", email: "", product: "", quantity: "", message: "" };

const inputCls =
  "w-full bg-transparent border-b border-navy/20 focus:border-gold outline-none py-3 text-navy placeholder:text-navy/35 text-[15px] transition-colors duration-300";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="text-[11px] tracking-[0.22em] uppercase text-navy/50">{label}</span>
    {children}
  </label>
);

const EnquiryForm = ({ initialProduct = "" }) => {
  const [form, setForm] = useState({ ...EMPTY, product: initialProduct });
  const [sending, setSending] = useState(false);
  const options = initialProduct && !PRODUCT_OPTIONS.includes(initialProduct)
    ? [initialProduct, ...PRODUCT_OPTIONS]
    : PRODUCT_OPTIONS;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      trackEvent("enquiry_submitted", { product: form.product, page_path: window.location.pathname });
      toast.success("Enquiry sent successfully. Our team will reach out shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send your enquiry. Please call or WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form data-testid="enquiry-form" onSubmit={submit} className="grid sm:grid-cols-2 gap-x-10 gap-y-8">
      <Field label="Name *" testid="field-name">
        <input data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
      </Field>
      <Field label="Company Name" testid="field-company">
        <input data-testid="enquiry-company-input" value={form.company} onChange={set("company")} placeholder="Hotel / business name" className={inputCls} />
      </Field>
      <Field label="Phone Number *" testid="field-phone">
        <input data-testid="enquiry-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
      </Field>
      <Field label="Email *" testid="field-email">
        <input data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
      </Field>
      <Field label="Product Interested In *" testid="field-product">
        <select data-testid="enquiry-product-select" required value={form.product} onChange={set("product")} className={`${inputCls} cursor-pointer`}>
          <option value="" disabled>Select a product</option>
          {options.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </Field>
      <Field label="Quantity / Requirement" testid="field-quantity">
        <input data-testid="enquiry-quantity-input" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 200 bed sheets" className={inputCls} />
      </Field>
      <div className="sm:col-span-2">
        <Field label="Message" testid="field-message">
          <textarea data-testid="enquiry-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your property and requirements..." className={`${inputCls} resize-none`} />
        </Field>
      </div>
      <div className="sm:col-span-2">
        <button
          data-testid="enquiry-submit-button"
          type="submit"
          disabled={sending}
          className="group inline-flex items-center gap-3 bg-navy text-ivory text-[13px] tracking-[0.12em] uppercase px-10 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy disabled:opacity-60"
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : null}
          Send Enquiry
          {!sending && <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />}
        </button>
      </div>
    </form>
  );
};

export default EnquiryForm;
